import { startOfDay } from 'date-fns';
import { useState } from 'react';
import { LuSettings2, LuShield } from 'react-icons/lu';

import { CashFlowConfig } from '@ndvr/portfolio-api';

import { useRunAllocation } from '@/cip/api/optimization';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EllipsisTooltip } from '@/components/ui/EllipsisTooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';
import { formatCurrency, formatDate, formatPercent, useRequiredParams } from 'utils';
import { useListCashflowConfigs } from 'views/portfolio/account-group-queries';
import {
    getPolicyWithdrawals,
    incompleteInflationNote,
    policyHorizon,
    totalPolicyWithdrawals,
    upcomingPolicyWithdrawals,
    withdrawalAccountName,
    withdrawalTitle,
} from 'views/portfolio/policy-withdrawals';
import { frequencyTypeToLabel } from 'views/portfolio/PortfolioPage/utils/frequencyToLabel';

import { diffList } from '../draft-diff';
import { usePolicyDraft, usePolicySaving } from '../PolicyChanges';
import { PolicyQueryError } from '../PolicyQueryError';
import {
    PolicyTable as Table,
    PolicyTableBody as TableBody,
    PolicyTableCell as TableCell,
    PolicyTableHead as TableHead,
    PolicyTableHeader as TableHeader,
    PolicyTableRow as TableRow,
} from '../PolicyTable';
import { usePolicyScope } from '../usePolicyScope';
import { lifestyleFromAccounts, sameLifestyle, summarizePolicySecuredTargets } from './policy-lifestyle';
import { PlanStat, PlanStats } from './PolicyPlanStats';
import { PolicyWithdrawalsManage } from './PolicyWithdrawalsManage';

const periods = [
    { years: 1, label: 'Next 12 Months' },
    { years: 3, label: 'Next 3 Years' },
    { years: 5, label: 'Next 5 Years' },
    { years: 10, label: 'Next 10 Years' },
];

const withdrawalId = (config: CashFlowConfig) => config.cashFlowConfigGUID;

type SecuredStatus = 'off' | 'unsaved' | 'loading' | 'notCalculated' | 'unavailable';

const SECURED_STATUS_TEXT: Record<SecuredStatus, string> = {
    off: '—',
    unsaved: 'After saving',
    loading: 'Loading…',
    notCalculated: 'Not calculated',
    unavailable: 'Unavailable',
};

const ProtectionBadge = ({ state }: { state: 'On' | 'Off' | '—' }) => (
    <span
        className={cn(
            'shrink-0 rounded-[4px] px-2 py-[3px] text-xs font-medium',
            state === 'On' ? 'bg-[#2F6468] text-white' : 'bg-[#F2EFEC] text-[#5E5552]',
        )}
    >
        {state}
    </span>
);

export const PolicyWithdrawals = () => {
    const { accountGUID, accountGroupGUID, globalAccountGUID } = useRequiredParams([
        'accountGUID',
        'accountGroupGUID',
        'globalAccountGUID',
    ]);
    const [years, setYears] = useState('1');
    const [managing, setManaging] = useState(false);
    const scope = usePolicyScope(accountGUID, accountGroupGUID);
    const configsQuery = useListCashflowConfigs(accountGroupGUID, undefined, { throwOnError: false });
    const allocationQuery = useRunAllocation(accountGroupGUID, { throwOnError: false });
    const isError = scope.status === 'error' || configsQuery.isLoadingError;
    const ready = scope.status === 'ready' && !configsQuery.isPending && !configsQuery.isLoadingError;
    const { accounts } = scope;
    const today = startOfDay(new Date());
    const savedWithdrawals = ready
        ? getPolicyWithdrawals(configsQuery.data ?? [], accounts, scope.profile.accountGroup)
        : [];
    const saving = usePolicySaving();
    const [draftWithdrawals, setDraftWithdrawals] = usePolicyDraft(
        'withdrawalPlan',
        savedWithdrawals,
        (draft, saved) => diffList(draft, saved, withdrawalId).count,
    );
    const savedLifestyle = lifestyleFromAccounts(
        accounts,
        ready ? scope.profile.accountGroup.targetEndDate : undefined,
    );
    const [lifestyle, setLifestyle] = usePolicyDraft('lifestyleProtection', savedLifestyle, (draft, saved) =>
        sameLifestyle(draft, saved) ? 0 : 1,
    );
    const withdrawals = upcomingPolicyWithdrawals(draftWithdrawals, today);
    const displayTotal = (amount: number) => {
        if (!ready) return '—';
        if (Number.isFinite(amount)) return formatCurrency(amount);
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span tabIndex={0} className="cursor-help underline decoration-dotted underline-offset-4">
                        Unavailable
                    </span>
                </TooltipTrigger>
                <TooltipContent>{incompleteInflationNote(withdrawals)}</TooltipContent>
            </Tooltip>
        );
    };
    // The header covers the whole projection (today until the target end date), the same horizon as Total secured.
    const horizon = policyHorizon(withdrawals, ready ? scope.profile.accountGroup.targetEndDate : undefined, today);
    const horizonLabel = horizon.endLabelDate
        ? `Total withdrawals to ${formatDate(horizon.endLabelDate, 'MMM yyyy')}`
        : 'Next 12 Months';
    const securedTargets = summarizePolicySecuredTargets(
        ready ? allocationQuery.data : undefined,
        accounts.map((item) => item.accountGUID),
    );
    // Nothing to calculate while protection is off; a newly enabled one only gets a target once it is saved.
    // Off always shows as off; the other states explain a target that is missing.
    const securedStatus: SecuredStatus =
        ready && !lifestyle.enabled
            ? 'off'
            : lifestyle.enabled && !savedLifestyle.enabled
              ? 'unsaved'
              : ready && allocationQuery.isPending
                ? 'loading'
                : securedTargets.notCalculated
                  ? 'notCalculated'
                  : 'unavailable';
    const securedValue = <T,>(value: T | undefined, format: (value: T) => string) =>
        securedStatus !== 'off' && value !== undefined ? format(value) : SECURED_STATUS_TEXT[securedStatus];
    const securedNote = (note: string) =>
        securedTargets.notCalculated ? 'The optimizer has no target for this portfolio’s protected account yet.' : note;
    const protectionKnown = ready && accounts.every((item) => Number.isFinite(Number(item.initialSecuredness)));
    const protectionState = protectionKnown ? (lifestyle.enabled ? 'On' : 'Off') : '—';

    const stats: PlanStat[] = [
        { label: horizonLabel, value: displayTotal(horizon.total) },
        {
            label: 'Total secured',
            value: securedValue(securedTargets.amount, formatCurrency),
            title: securedNote('Calculated target allocation to the secured bond sleeve, not current holdings.'),
        },
        {
            label: 'Lifestyle protection',
            value: (
                <>
                    {securedValue(securedTargets.percentage, (percentage) =>
                        percentage === 'mixed' ? 'Varies by account' : formatPercent(percentage * 100, 1),
                    )}
                    <ProtectionBadge state={protectionState} />
                </>
            ),
            title: securedNote(
                'Calculated target securedness, not current holdings. Different account targets are not averaged.',
            ),
        },
    ];

    return (
        <div className="space-y-4.5">
            <div className="grid items-center gap-4 xl:min-h-14 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                <h2 id="policy-withdrawal-title" className="text-cocoa-1000 text-[22px] font-medium">
                    Withdrawal Plan
                </h2>
                <PlanStats stats={stats} className="justify-center text-center xl:col-start-2" />
                <Button
                    variant="outline"
                    className="justify-self-end xl:col-start-3"
                    disabled={!ready || saving}
                    onClick={() => setManaging(true)}
                >
                    <LuSettings2 aria-hidden="true" className="size-4" />
                    Manage
                </Button>
            </div>
            {allocationQuery.isLoadingError && (
                <PolicyQueryError message="Couldn’t load lifestyle protection targets." queries={[allocationQuery]} />
            )}
            {isError ? (
                <PolicyQueryError
                    message="Couldn’t load withdrawal plan."
                    queries={[...scope.retryQueries, configsQuery]}
                />
            ) : (
                <Table
                    className="min-w-[720px] table-fixed"
                    footer={
                        <>
                            <div className="flex flex-wrap items-center gap-3">
                                <label htmlFor="withdrawal-total-period" className="text-cocoa-700 text-sm">
                                    Total projected withdrawals over
                                </label>
                                <Select value={years} onValueChange={setYears}>
                                    <SelectTrigger
                                        id="withdrawal-total-period"
                                        className="h-8 w-44 border-0 bg-transparent px-2 font-medium shadow-none"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {periods.map((period) => (
                                            <SelectItem key={period.years} value={String(period.years)}>
                                                {period.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <span className="text-cocoa-1000 font-medium tabular-nums" aria-live="polite">
                                {displayTotal(totalPolicyWithdrawals(withdrawals, Number(years), today))}
                            </span>
                        </>
                    }
                >
                    <colgroup>
                        <col className="w-[30%]" />
                        <col className="w-[18%]" />
                        <col className="w-[18%]" />
                        <col className="w-[20%]" />
                        <col className="w-[14%]" />
                    </colgroup>
                    <TableHeader>
                        <TableRow>
                            {['Purpose', 'Starts', 'Frequency', 'From account', 'Amount'].map((label) => (
                                <TableHead key={label} className={label === 'Amount' ? 'text-right' : undefined}>
                                    {label}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!ready || withdrawals.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-cocoa-500 py-8 text-center" role="status">
                                    {ready ? 'No withdrawals scheduled.' : 'Loading withdrawal plan…'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            withdrawals.map((config) => (
                                <TableRow key={config.cashFlowConfigGUID}>
                                    <TableCell>
                                        <EllipsisTooltip className="font-medium">
                                            {withdrawalTitle(config)}
                                        </EllipsisTooltip>
                                    </TableCell>
                                    <TableCell className="text-cocoa-800 whitespace-nowrap">
                                        {formatDate(config.factorDateFrame.startDate, 'MMM d, yyyy')}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {frequencyTypeToLabel(config.factorDateFrame.frequencyType)}
                                    </TableCell>
                                    <TableCell>{withdrawalAccountName(accounts, config.accountGUID)}</TableCell>
                                    <TableCell className="text-right font-medium tabular-nums">
                                        {formatCurrency(config.amount)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            )}
            <Card
                aria-labelledby="withdrawal-lifestyle-protection-title"
                className="flex flex-row items-center gap-3.5 rounded-[10px] border-stone-200 px-5 py-3.5 shadow-none"
            >
                <div className="bg-cocoa-1000 flex size-9 shrink-0 items-center justify-center rounded-lg text-white">
                    <LuShield aria-hidden="true" className="size-5" />
                </div>
                <h3
                    id="withdrawal-lifestyle-protection-title"
                    className="text-cocoa-1000 min-w-0 flex-1 text-[15px] font-medium"
                    title="Holds upcoming withdrawals in short-term treasuries so they are secured from market swings."
                >
                    Lifestyle protection
                </h3>
                <ProtectionBadge state={protectionState} />
            </Card>
            <p className="text-cocoa-500 text-xs">
                Projected net withdrawals, including configured inflation adjustments. Amounts shown per occurrence
                above are the base amounts. Secured values are calculated allocation targets, not current holdings.
            </p>
            {ready && (
                <PolicyWithdrawalsManage
                    open={managing}
                    onOpenChange={setManaging}
                    accountGroupGUID={accountGroupGUID}
                    globalAccountGUID={globalAccountGUID}
                    withdrawals={withdrawals}
                    allWithdrawals={draftWithdrawals}
                    stats={stats}
                    lifestyle={lifestyle}
                    onLifestyleChange={setLifestyle}
                    accounts={accounts}
                    onSave={(config) =>
                        setDraftWithdrawals((current) =>
                            current.some((item) => withdrawalId(item) === withdrawalId(config))
                                ? current.map((item) => (withdrawalId(item) === withdrawalId(config) ? config : item))
                                : [...current, config],
                        )
                    }
                    onDelete={(guid) =>
                        setDraftWithdrawals((current) => current.filter((item) => withdrawalId(item) !== guid))
                    }
                />
            )}
        </div>
    );
};
